import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CardActions, CardContent, CardHeader, CircularProgress, Stack, Tooltip } from '@mui/material';
import { ChevronRight, HelpOutline, LockReset, OpenInNew, PrivacyTipOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import { orange } from '@mui/material/colors';
import axios from 'axios';
import { toast } from 'react-toastify';
import ErrorBoundary from '../Common/ErrorBoundary';
import ForgotPassword from './ForgotPassword';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  border: 'none',
  fontFamily: 'inherit',
  gap: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '550px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const FooterLink = styled(Link)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.625),
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  transition: theme.transitions.create(['color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '&:hover': {
    color: orange[800],
  },
  '& .MuiSvgIcon-root': {
    fontSize: '0.95rem',
  },
}));

const FooterNav = styled('nav')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
}));

const FooterDot = styled('span')(({ theme }) => ({
  width: 3,
  height: 3,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.text.secondary, 0.35),
  flexShrink: 0,
}));

const ResetPasswordTrigger = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75),
  width: '100%',
  padding: theme.spacing(1.75, 2),
  margin: 0,
  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.grey[50], 0.95),
  cursor: 'pointer',
  transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover:not(:disabled)': {
    borderColor: alpha(orange[400], 0.8),
    backgroundColor: alpha(orange[50], 0.65),
    boxShadow: `0 4px 14px ${alpha(orange[800], 0.08)}`,
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
}));

export default function SignInCard() {
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = React.useState('');

  const handleClickOpen = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const emailInput = document.getElementById('email') as HTMLInputElement | null;
    setForgotPasswordEmail(emailInput?.value.trim() || '');
    setEmailError(false);
    setEmailErrorMessage('');
    setPasswordError(false);
    setPasswordErrorMessage('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const validateInputs = () => {
    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleSubmit = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateInputs()) {
      return;
    }

    setLoading(true);
    const data = new FormData(event.currentTarget as HTMLFormElement);
    const email = data.get('email');
    const password = data.get('password');
    const res = await axios.get('/csrf');

    axios.defaults.headers.post['xsrfCookieName'] = 'csrftoken';
    axios.defaults.headers.post['X-CSRFToken'] = res.data.csrfToken;
    axios.defaults.headers.post['Content-Type'] = 'multipart/form-data';

    axios.post('/sign_in/', {
      email: email,
      password: password,
    }).then(response => {
      localStorage.removeItem('entity_id');
      localStorage.removeItem('entity_nm');
      localStorage.removeItem('user');
      setTimeout(() => {
        window.location.href = response.data.url;
      }, 100);
    }).catch(error => {
      setLoading(false);
      setTimeout(() => {
        toast.error(error.response?.data?.message, { position: 'top-center' });
      }, 100);
    });
  };

  return (
    <ErrorBoundary fallback={<h1>Sorry.. there was an error.</h1>}>
      <Card variant="outlined" sx={{ borderRadius: 1.25 }}>
        <CardHeader
          sx={{ pt: 3, pb: 1.5, px: { xs: 2.5, sm: 4 } }}
          title={
            <Stack direction="column" alignItems="center" justifyContent="center" fontFamily="inherit" spacing={1.5}>
              <Box
                component="img"
                src="https://idea4industry.com/wp-content/uploads/cropped-IDEA-favicon-192x192.png"
                alt="IDEA Data Whispers"
                sx={{ width: 56, height: 56 }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  fontFamily: 'inherit',
                  maxWidth: '90%',
                  textAlign: 'center',
                  fontSize: 'clamp(1.3rem, 10vw, 1.5rem)',
                }}
              >
                <Link href="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                  IDEA Data Whispers
                </Link>
              </Typography>
            </Stack>
          }
        />
        <Divider sx={{ mx: { xs: 2.5, sm: 4 } }} />
        <CardContent sx={{ pt: 3, pb: 2, px: { xs: 2.5, sm: 4 } }}>
          <Stack spacing={0.75} sx={{ mb: 3, textAlign: 'center' }}>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontFamily: 'inherit',
                fontWeight: 'bold',
                fontSize: 'clamp(1.5rem, 10vw, 1.75rem)',
              }}
            >
              Sign In
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.9rem', lineHeight: 1.5, px: 1 }}
            >
              Sign in to access your account and insights dashboard.
            </Typography>
          </Stack>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2.5 }}
          >
            <FormControl sx={{ gap: 0.5 }}>
              <TextField
                variant="standard"
                error={emailError}
                helperText={emailErrorMessage}
                label="Email *"
                id="email"
                type="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                fullWidth
                disabled={loading}
                color={emailError ? 'error' : 'primary'}
                slotProps={{
                  input: {
                    sx: {
                      '& .MuiInputBase-input': {
                        padding: '5px 5px',
                        borderRadius: '5px',
                      },
                    },
                  },
                }}
              />
            </FormControl>
            <FormControl sx={{ gap: 0.5 }}>
              <TextField
                variant="standard"
                error={passwordError}
                helperText={passwordErrorMessage}
                label="Password *"
                name="password"
                placeholder="••••••"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                fullWidth
                disabled={loading}
                color={passwordError ? 'error' : 'primary'}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowPassword((prev) => !prev)}
                            onMouseDown={(event) => event.preventDefault()}
                            edge="end"
                            disabled={loading}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    sx: {
                      '& .MuiInputBase-input': {
                        padding: '5px 5px',
                        borderRadius: '5px',
                      },
                    },
                  },
                }}
              />
            </FormControl>

            <Button
                type="submit"
                fullWidth
                variant="contained"
                loading={loading}
                disabled={loading}
                loadingPosition="end"
                loadingIndicator={
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                    Signing in...
                    <CircularProgress variant="indeterminate" size={16} sx={{ color: 'white' }} />
                  </Typography>
                }
                style={{
                  textTransform: 'none',
                  backgroundColor: orange[800],
                  fontWeight: 'bold',
                  width: '100%',
                  margin: '0 auto',
                  height: 50,
                  border: 0,
                  boxShadow: 'none',
                  fontSize: 'clamp(1.1rem, 10vw, 1rem)',
                }}
              >
                {!loading && 'Sign In'}
              </Button>
          </Box>

          <Stack spacing={2} sx={{ mt: 2.5 }}>
            <ResetPasswordTrigger
              type="button"
              onClick={handleClickOpen}
              disabled={loading}
              aria-label="Open password reset dialog"
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(orange[50], 0.95),
                  color: orange[800],
                  flexShrink: 0,
                }}
              >
                <LockReset fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Forgot your password?
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                  We&apos;ll email you a secure, time-limited reset link
                </Typography>
              </Box>
              <ChevronRight sx={{ color: 'text.secondary', flexShrink: 0 }} />
            </ResetPasswordTrigger>
            <ForgotPassword
              open={open}
              handleClose={handleClose}
              initialEmail={forgotPasswordEmail}
            />
          </Stack>
        </CardContent>
        <Divider sx={{ mx: { xs: 2.5, sm: 4 }, opacity: 0.55 }} />
        <CardActions sx={{ justifyContent: 'center', flexDirection: 'column', pt: 1.5, pb: 3, px: { xs: 2.5, sm: 4 }, gap: 1.25 }}>
          <FooterNav aria-label="Legal and support">
            <FooterLink
              href="https://idea4industry.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <PrivacyTipOutlined aria-hidden />
              Privacy Policy
              <OpenInNew aria-hidden sx={{ fontSize: '0.75rem !important', opacity: 0.65 }} />
            </FooterLink>
            <FooterDot aria-hidden />
            <FooterLink
              href="/help"
            >
              <HelpOutline aria-hidden />
              Help
            </FooterLink>
          </FooterNav>
          <Typography
            id="copyright-text"
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: 'inherit', textAlign: 'center', width: '100%', fontSize: '0.75rem', opacity: 0.85 }}
          >
            {`© ${new Date().getFullYear()}, All rights reserved by IDEA`}
          </Typography>
        </CardActions>
      </Card>
    </ErrorBoundary>
  );
}
